import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import { CURRENCIES } from '../../../constants/currenciesConstants';

import styles from './TrackerListView.module.scss';

const STATUS_COLORS = {
  new: '#6dccb1',
  applied: '#79aad9',
  interview: '#f1d86f',
  offer: '#F9B74E',
  hired: '#64CE92',
  hired_and_archived: '#64CE92',
  passed: '#8a8a8a',
  declined: '#8a8a8a',
  expired: '#8a8a8a',
  not_your_refer: '#8a8a8a',
  archived: '#E85050',
};

const getStatusLabels = (t) => ({
  new: t('tracker.statusNew', 'New'),
  applied: t('tracker.applied', 'Applied'),
  interview: t('tracker.interviewing', 'Interviewing'),
  offer: t('tracker.statusOffer', 'Offer'),
  hired: `✓ ${t('tracker.statusHired', 'Hired')}`,
  hired_and_archived: `✓ ${t('tracker.statusHired', 'Hired')}`,
  passed: t('tracker.statusPassed', 'Passed'),
  declined: t('tracker.statusDeclined', 'Declined'),
  expired: t('tracker.statusExpired', 'Expired'),
  not_your_refer: t('tracker.statusReferralDeclined', 'Referral Declined'),
  archived: `✗ ${t('tracker.statusRejected', 'Rejected')}`,
});

/** Resolve interview stage label from job.interview_stages */
const resolveInterviewStageLabel = (job, candidateStatus, columnStatus, fallback) => {
  if (columnStatus === 'interview' || candidateStatus?.includes('interview')) {
    const stages = job?.interview_stages;
    if (stages && Array.isArray(stages)) {
      const match = stages.find((s) => s.value === candidateStatus);
      if (match) return match.label;
    }
    return fallback;
  }
  return null;
};

/** Format reward amount with correct currency symbol */
const formatReward = (rewards) => {
  if (!rewards?.monetary) return '-';
  const symbol = CURRENCIES[rewards.currency_names] || '$';
  return `${symbol}${Number(rewards.monetary).toLocaleString()}`;
};

const TrackerListView = ({ columns, direction, isLoading }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const statusLabels = useMemo(() => getStatusLabels(t), [t]);

  // Flatten all columns into a single list
  const allJobs = useMemo(() => {
    const jobs = [];
    columns.forEach((col) => {
      col.jobs.forEach((job) => {
        jobs.push({ ...job, columnStatus: col.status });
      });
    });
    return jobs;
  }, [columns]);

  const handleRowClick = (job) => {
    const firstReferral = job.referrals?.[0];
    if (!firstReferral) return;

    const params = new URLSearchParams({
      jobId: job.uuid || job.id,
      referralId: firstReferral.uuid || firstReferral.id || '',
      requestId: firstReferral.request_uuid || firstReferral.request_id || '',
      direction,
      status: firstReferral?.candidate_status || job.columnStatus,
    });
    navigate(`/tracker/details?${params.toString()}`);
  };

  const renderHeaders = () => (
    <thead>
      <tr>
        {direction === 'their' && <th>{t('tracker.candidate', 'Candidate')}</th>}
        <th>{t('tracker.jobTitle', 'Job Title')}</th>
        <th>{t('tracker.company', 'Company')}</th>
        {direction === 'their' && <th>{t('tracker.rewards', 'Rewards')}</th>}
        {direction === 'my' && <th>{t('tracker.referredBy', 'Referred By')}</th>}
        <th>{t('tracker.stage', 'Stage')}</th>
      </tr>
    </thead>
  );

  if (isLoading) {
    return (
      <div className={styles.container}>
        <table className={styles.table}>
          {renderHeaders()}
          <tbody>
            {Array.from({ length: 8 }).map((unused, i) => (
              <tr key={i} className={styles.skeletonRow}>
                {Array.from({ length: direction === 'their' ? 5 : 4 }).map((unusedCol, j) => (
                  <td key={j}>
                    <div className={styles.skeleton} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (!allJobs.length) {
    return (
      <div className={styles.empty}>
        <p>{t('tracker.noResults', 'No jobs found')}</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <table className={styles.table}>
        {renderHeaders()}
        <tbody>
          {allJobs.map((job, index) => {
            const firstReferral = job.referrals?.[0];
            const candidateStatus = firstReferral?.candidate_status || job.columnStatus;
            const interviewLabel = resolveInterviewStageLabel(
              job,
              candidateStatus,
              job.columnStatus,
              t('tracker.interviewing', 'Interviewing'),
            );
            const statusLabel = interviewLabel || statusLabels[candidateStatus] || candidateStatus;
            const statusColor = interviewLabel
              ? STATUS_COLORS.interview
              : STATUS_COLORS[candidateStatus] || '#d3dae6';

            return (
              <tr
                key={`${job.id || job.uuid}-${index}`}
                className={styles.row}
                onClick={() => handleRowClick(job)}
              >
                {direction === 'their' && (
                  <td>
                    <span className={styles.candidateName}>{firstReferral?.full_name || '-'}</span>
                  </td>
                )}
                <td>
                  <span className={styles.jobTitle}>{job.title}</span>
                </td>
                <td>{job.organization_name}</td>
                {direction === 'their' && (
                  <td>{formatReward(job.rewards)}</td>
                )}
                {direction === 'my' && (
                  <td>
                    {firstReferral?.id === job.user_id
                      ? t('tracker.directApplication', 'Direct Application')
                      : firstReferral?.full_name || '-'}
                  </td>
                )}
                <td>
                  <span
                    className={styles.stageLabel}
                    style={{ backgroundColor: `${statusColor}22`, color: statusColor }}
                  >
                    {statusLabel}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TrackerListView;
